import {
    Add,
    FilterAltOffOutlined,
    FilterAltOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    Grid,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GET } from "../../Utilities/BaseService";
import {
    filterSelectionMessage,
    hasValue,
    noDataMessage,
} from "../../Utilities/constants";
import { MultiPermissionAuthorize } from "../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../Utilities/UpdatedSearchableInput";
import BaseTable from "../Table/BaseTable";
import SearchBar from "../Table/Components/SearchBar";

type Filter = {
    search: string;
    district: number[] | "" | "all";
};

const initialFilter: Filter = {
    search: "",
    district: "",
};

const PowerStation = () => {
    const [rowsCount, setRowsCount] = useState(0);
    const [pagination, setPagination] = useState<any>({ page: 0, limit: 100 });
    const [filter, setFilter] = useState<any>(initialFilter);
    const [intermediateFilter, setIntermediateFilter] =
        useState<Filter>(initialFilter);
    const [changeCount, setChangeCount] = useState(0);

    const [clearAll, setClearAll] = useState({
        district: false,
        powerstation: false,
        search: false,
    });
    const [feedback, setFeedback] = useState("");
    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));

    const getPowerStations = () => {
        return GET("/powerstation", {
            page: pagination.page + 1,
            limit: pagination.limit,
            search: filter.search,
            district: filter.district ? filter.district : undefined,
        });
    };

    const { data, isLoading } = useQuery(
        ["powerstations", pagination.page, pagination.limit, filter],
        getPowerStations,
        {
            onSuccess(res) {
                if (res) setRowsCount(res?.data.count);
            },
            enabled: Boolean(changeCount),
        }
    );

    const handleClearAll = () => {
        // reset UI filters
        setIntermediateFilter(initialFilter);

        // reset applied filters
        setFilter(initialFilter);

        // notify child inputs
        setClearAll({
            district: true,
            powerstation: true,
            search: true,
        });

        // reset search trigger & pagination
        setChangeCount(0);
        setPagination({ page: 0, limit: 100 });

        // turn flags off after render
        setTimeout(
            () =>
                setClearAll({
                    district: false,
                    powerstation: false,
                    search: false,
                }),
            0
        );
    };
    useEffect(() => {
        if (changeCount) {
            setFilter(intermediateFilter);
        }
    }, [changeCount]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: mdDown ? "auto" : `calc(100vh - 96px)`,
            }}
        >
            <Card
                elevation={0}
                sx={{
                    bgcolor: theme.palette.common.white,
                    p: 2,
                    mb: "1rem",
                }}
            >
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1.5}
                >
                    <Grid item xs={12} md="auto">
                        <Typography color="GrayText" variant="h5">
                            Powerstations
                        </Typography>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md={6}
                        container
                        justifyContent="flex-end"
                        spacing={1.5}
                    >
                        <Grid item xs={12} md="auto">
                            <MultiPermissionAuthorize
                                ops={["CREATE POWERSTATION"]}
                            >
                                <Link
                                    to="/powerstations/add"
                                    style={{ textDecoration: "none" }}
                                >
                                    <Button
                                        sx={{ width: "28ch", height: "100%" }}
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<Add />}
                                        fullWidth
                                    >
                                        add powerstation
                                    </Button>
                                </Link>
                            </MultiPermissionAuthorize>
                        </Grid>
                    </Grid>

                    <Grid container xs={12} spacing={1} mt={1}>
                        <Grid item xs={12} md={2}>
                            <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                                <UpdatedSearchableInput
                                    label="Districts"
                                    _name="district"
                                    api="/region/district"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    clearAll={clearAll.district}
                                />
                            </MultiPermissionAuthorize>
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <SearchBar
                                filter={intermediateFilter}
                                setFilter={setIntermediateFilter}
                                clear={clearAll.search}
                            />
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <Button
                                color="secondary"
                                variant="outlined"
                                onClick={() => setChangeCount(changeCount + 1)}
                                sx={{ width: "100%", height: "100%" }}
                                startIcon={<FilterAltOutlined />}
                                fullWidth
                            >
                                Search
                            </Button>
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <Button
                                color="error"
                                variant="outlined"
                                onClick={handleClearAll}
                                sx={{ width: "100%", height: "100%" }}
                                startIcon={<FilterAltOffOutlined />}
                                fullWidth
                            >
                                Clear All
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Card>

            {data?.data.rows.length > 0 ? (
                <Box sx={{ height: "60%" }}>
                    <BaseTable
                        headers={{
                            id: "ID",
                            name: "Name",
                            districtName: "District",
                        }}
                        data={data?.data.rows.map((row: any) => ({
                            ...row,
                            districtName: row.district.name,
                        }))}
                        load={Boolean(changeCount)}
                        defaultSelectedHeaders={["id", "name", "districtName"]}
                        delEndPoint="/powerstation"
                        rowsCount={rowsCount}
                        pagination={pagination}
                        setPagination={setPagination}
                        isLoading={isLoading}
                        feedback={feedback}
                        setFeedback={setFeedback}
                        permissions={{
                            edit: ["UPDATE POWERSTATION"],
                            delete: ["DELETE POWERSTATION"],
                        }}
                        queryKey={[
                            "powerstations",
                            pagination.page,
                            pagination.limit,
                            filter,
                        ]}
                        reportDetails={{
                            district: filter.district
                                ? filter.district
                                : undefined,
                            powerstation: filter.powerstation
                                ? filter.powerstation
                                : undefined,
                            village: filter.village
                                ? filter.village
                                : undefined,
                            hamlet: filter.hamlet ? filter.hamlet : undefined,
                        }}
                        reportName="Powerstations__List"
                    />
                </Box>
            ) : hasValue(filter) ? (
                <Alert severity="info">{noDataMessage}</Alert>
            ) : (
                <Alert severity="info">{filterSelectionMessage}</Alert>
            )}
        </Box>
    );
};

export default PowerStation;
